import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Eye, EyeOff, FileText, Gem, Globe, Loader2, MessageSquare, Plus, Shield,
  Sparkles, Star, Trash2, Zap,
} from 'lucide-react';
import { StarRating } from '@/components/ui/star-icon';
import { toast } from 'sonner';
import { api, type ManualReviewInput, type PartnerReview, type ReviewsConfig, type ReviewStatusFilter } from '@/lib/api';
import { SegmentControl } from '@/components/ui/segment-control';
import { BottomSheet } from '@/components/ui/dialog';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonParagraph } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { AccentPicker } from '@/components/AccentPicker';
import { getWebApp, haptic } from '@/lib/telegram';

export const Route = createFileRoute('/bot-reviews/$botId')({ component: BotReviews });

type Tab = 'list' | 'design';

/** Значки бренда в шапке витрины. Имена совпадают с `GLYPHS` на бэке. */
const GLYPHS = [
  { key: 'shield-check', icon: Shield, label: 'Щит' },
  { key: 'zap', icon: Zap, label: 'Молния' },
  { key: 'globe', icon: Globe, label: 'Глобус' },
  { key: 'gem', icon: Gem, label: 'Камень' },
  { key: 'sparkles', icon: Sparkles, label: 'Искры' },
] as const;

function BotReviews() {
  const { botId } = Route.useParams();
  const [tab, setTab] = useState<Tab>('list');
  const navigate = useNavigate();

  // Нативная «назад» Telegram, как на соседних экранах бота. Без неё выйти можно было
  // только маленькой стрелкой. Задержка та же, что в карточке бота: тап, открывший
  // экран, не должен сразу сработать как «назад».
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    const onBack = () => void navigate({ to: '/bot' });
    const timer = window.setTimeout(() => {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
    }, 300);
    return () => {
      window.clearTimeout(timer);
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate, botId]);

  return (
    <div className="p-4 pb-28 space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3">
        <Link
          to="/bot"
          aria-label="Назад к боту"
          className="relative w-9 h-9 rounded-full surface-inset grid place-items-center shrink-0 after:absolute after:-inset-[5px] after:content-['']"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </Link>
        <h2 className="font-display text-lg font-semibold tracking-tight">Отзывы</h2>
      </div>

      <SegmentControl<Tab>
        value={tab}
        onChange={setTab}
        options={[
          { value: 'list', label: 'Отзывы' },
          { value: 'design', label: 'Оформление' },
        ]}
      />

      {tab === 'list' ? <ReviewsList botId={botId} /> : <DesignPane botId={botId} />}
    </div>
  );
}

// ---------------------------------------------------------------- список

function ReviewsList({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ReviewStatusFilter>('published');
  const [formOpen, setFormOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<PartnerReview | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['reviews', botId, status],
    queryFn: () => api.reviews(botId, { status }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['reviews', botId] });

  const moderate = useMutation({
    mutationFn: ({ id, next }: { id: string; next: 'published' | 'hidden' }) =>
      api.moderateReview(botId, id, next),
    onSuccess: (_r, v) => {
      haptic('success');
      toast.success(v.next === 'hidden' ? 'Отзыв скрыт с витрины' : 'Отзыв вернулся на витрину');
      invalidate();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteReview(botId, id),
    onSuccess: () => {
      haptic('success');
      toast.success('Отзыв удалён');
      invalidate();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message);
    },
  });

  const counts = data?.counts;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <SegmentControl<ReviewStatusFilter>
          value={status}
          onChange={setStatus}
          options={[
            { value: 'published', label: `На витрине${counts ? ` ${counts.published}` : ''}` },
            { value: 'hidden', label: `Скрытые${counts ? ` ${counts.hidden}` : ''}` },
          ]}
        />
      </div>

      <button
        type="button"
        onClick={() => {
          haptic('light');
          setFormOpen(true);
        }}
        className="w-full surface-inset rounded-[14px] py-3 text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
      >
        <Plus className="w-4 h-4" /> Добавить отзыв
      </button>

      {isPending ? (
        <ReviewsSkeleton />
      ) : !data?.items.length ? (
        <p className="text-sm text-muted-foreground text-center py-10">
          {status === 'hidden' ? 'Скрытых отзывов нет.' : 'Отзывов пока нет.'}
        </p>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => (
            <ReviewRow
              key={r.id}
              review={r}
              busy={moderate.isPending || remove.isPending}
              onToggle={() =>
                moderate.mutate({ id: r.id, next: r.status === 'hidden' ? 'published' : 'hidden' })
              }
              onDelete={() => remove.mutate(r.id)}
              onReply={() => setReplyTo(r)}
            />
          ))}
        </div>
      )}

      <ReplySheet
        botId={botId}
        review={replyTo}
        onClose={() => setReplyTo(null)}
        onSaved={invalidate}
      />

      <ManualReviewSheet
        botId={botId}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={invalidate}
      />
    </div>
  );
}

function ReviewRow({
  review,
  busy,
  onToggle,
  onDelete,
  onReply,
}: {
  review: PartnerReview;
  busy: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onReply: () => void;
}) {
  const hidden = review.status === 'hidden';
  return (
    <div className={`surface-card rounded-[18px] p-4 space-y-3 ${hidden ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold truncate">{review.author_label}</span>
            {review.source === 'partner' && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-white/[0.08] text-muted-foreground">
                вручную
              </span>
            )}
            {hidden && (
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-white/[0.08] text-muted-foreground">
                скрыт
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {new Date(review.created_at).toLocaleDateString('ru-RU')}
            {review.subject_title && ` · ${review.subject_title}`}
          </div>
        </div>
        <div className="flex gap-0.5 shrink-0">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={s}
              className={`w-3.5 h-3.5 ${s <= review.rating ? 'text-foreground' : 'text-white/15'}`}
              fill="currentColor"
              strokeWidth={0}
            />
          ))}
        </div>
      </div>

      {review.text && <p className="text-[13px] leading-relaxed text-foreground/85 [overflow-wrap:anywhere]">{review.text}</p>}

      {review.reply_text && (
        <div className="surface-inset rounded-[12px] px-3 py-2.5">
          <div className="text-[10.5px] font-bold uppercase text-muted-foreground mb-1">
            Ваш ответ
            {review.replied_at && ` · ${new Date(review.replied_at).toLocaleDateString('ru-RU')}`}
          </div>
          {/* Ссылка в ответе без пробелов раздвигала экран вбок. */}
          <p className="text-[13px] leading-relaxed text-foreground/85 [overflow-wrap:anywhere]">{review.reply_text}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onReply}
          disabled={busy}
          className="flex-1 surface-inset rounded-[12px] py-2 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98] transition-transform"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {review.reply_text ? 'Изменить ответ' : 'Ответить'}
        </button>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          className="flex-1 surface-inset rounded-[12px] py-2 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98] transition-transform"
        >
          {hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {hidden ? 'Вернуть на витрину' : 'Скрыть'}
        </button>
        {/* Удалять можно только свои, заведённые руками: у клиентского отзыва
            «скрыть» не должно превращаться в «стереть». */}
        {review.source === 'partner' && (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="shrink-0 surface-inset rounded-[12px] px-3 text-xs text-danger disabled:opacity-50 active:scale-[0.98] transition-transform"
            aria-label="Удалить отзыв"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Ответ на отзыв. Публичный: его увидят все читатели витрины, автору уйдёт
 *  сообщение в бота (один раз, правка ответа второго сообщения не шлёт). */
function ReplySheet({
  botId,
  review,
  onClose,
  onSaved,
}: {
  botId: string;
  review: PartnerReview | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (review) setText(review.reply_text ?? '');
  }, [review]);

  const save = useMutation({
    mutationFn: (value: string) => api.replyReview(botId, review!.id, value),
    onSuccess: (_r, value) => {
      haptic('success');
      toast.success(value.trim() ? 'Ответ опубликован' : 'Ответ удалён');
      onSaved();
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message);
    },
  });

  const hadReply = Boolean(review?.reply_text);
  return (
    <BottomSheet open={review !== null} onClose={onClose} title="Ответ на отзыв">
      <div className="space-y-4">
        {review && (
          <div className="surface-inset rounded-[13px] px-4 py-3">
            <div className="text-[11px] text-muted-foreground mb-1">{review.author_label}</div>
            <p className="text-[13px] leading-relaxed text-foreground/85">
              {review.text || <StarRating value={review.rating} />}
            </p>
          </div>
        )}
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Ответ увидят все под отзывом, а автору придёт сообщение в бота.
        </p>
        <Field label="Ваш ответ">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="Спасибо за отзыв! Если что-то не так, напишите в поддержку."
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none resize-none"
          />
        </Field>
        <div className="flex gap-2">
          {hadReply && (
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate('')}
              className="shrink-0 surface-inset rounded-[14px] px-4 text-sm text-danger disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              Удалить
            </button>
          )}
          <button
            type="button"
            disabled={save.isPending || !text.trim()}
            onClick={() => save.mutate(text)}
            className="flex-1 calm-cta rounded-[14px] py-3 text-sm font-bold disabled:opacity-50 active:scale-[0.99] transition-transform flex items-center justify-center gap-2"
          >
            {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {hadReply ? 'Сохранить' : 'Опубликовать'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

function ManualReviewSheet({
  botId,
  open,
  onClose,
  onSaved,
}: {
  botId: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ManualReviewInput>({ author_label: '', rating: 5 });
  const set = <K extends keyof ManualReviewInput>(k: K, v: ManualReviewInput[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) setForm({ author_label: '', rating: 5 });
  }, [open]);

  const save = useMutation({
    mutationFn: () => api.createManualReview(botId, form),
    onSuccess: () => {
      haptic('success');
      toast.success('Отзыв добавлен на витрину');
      onSaved();
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message);
    },
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Свой отзыв">
      <div className="space-y-4">
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Такой отзыв выглядит на витрине обычным, но значка «подтверждённая подписка» у него нет:
          покупки за ним не стоит.
        </p>

        <Field label="Имя автора">
          <input
            value={form.author_label}
            onChange={(e) => set('author_label', e.target.value)}
            maxLength={64}
            placeholder="Мария К."
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>

        <Field label="Оценка">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => set('rating', s)}
                className="w-11 h-11 grid place-items-center rounded-xl active:scale-90 transition-transform"
                aria-label={`Оценка ${s}`}
              >
                <Star
                  className={`w-7 h-7 ${s <= (form.rating ?? 0) ? 'text-foreground' : 'text-white/15'}`}
                  fill="currentColor"
                  strokeWidth={0}
                />
              </button>
            ))}
          </div>
        </Field>

        <Field label="Текст">
          <textarea
            value={form.text ?? ''}
            onChange={(e) => set('text', e.target.value.slice(0, 500))}
            rows={3}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm resize-none focus:outline-none"
          />
        </Field>

        {/* Цены нет: витрина её не показывает ни у какого отзыва (заявка #117). */}
        <Field label="Тариф">
          <input
            value={form.subject_title ?? ''}
            onChange={(e) => set('subject_title', e.target.value)}
            maxLength={64}
            placeholder="Подписка на 3 месяца"
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>

        <Field label="Месяцев с вами" hint="Подпись под ником. Оставьте пустым, если не нужна.">
          <input
            type="number"
            min={0}
            max={240}
            value={form.months_with_us ?? ''}
            onChange={(e) => set('months_with_us', e.target.value ? Number(e.target.value) : null)}
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>

        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!form.author_label.trim() || save.isPending}
          className="w-full bg-primary text-primary-foreground py-3.5 rounded-[14px] text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.99] transition-transform"
        >
          {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Добавить на витрину
        </button>
      </div>
    </BottomSheet>
  );
}

// ---------------------------------------------------------------- оформление

function DesignPane({ botId }: { botId: string }) {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['reviews-config', botId],
    queryFn: () => api.reviewsConfig(botId),
  });

  const save = useMutation({
    mutationFn: (patch: Partial<ReviewsConfig>) => api.updateReviewsConfig(botId, patch),
    onSuccess: (next) => {
      haptic('success');
      qc.setQueryData(['reviews-config', botId], next);
      toast.success('Сохранено');
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(e.message);
    },
  });

  const [rewardPercent, setRewardPercent] = useState('10');
  const [promoTitle, setPromoTitle] = useState('');
  const [promoSubtitle, setPromoSubtitle] = useState('');
  useEffect(() => {
    if (!data) return;
    setRewardPercent(String(data.reward?.percent ?? 10));
    setPromoTitle(data.promo.title);
    setPromoSubtitle(data.promo.subtitle);
  }, [data]);

  if (isPending || !data) return <SkeletonCard className="h-[400px] rounded-[18px]" />;

  return (
    <div className="space-y-4">
      <section className="surface-card rounded-[18px] p-4 space-y-4">
        <Row
          title="Отзывы включены"
          hint="Выключите, и раздел исчезнет из мини-аппа клиентов."
          checked={data.enabled}
          onChange={(v) => save.mutate({ enabled: v })}
        />
        <Row
          title="Значок «подтверждённая подписка»"
          hint="Галочка у тех, кто действительно оплатил."
          checked={data.verified_badge}
          onChange={(v) => save.mutate({ verified_badge: v })}
        />
      </section>

      <section className="surface-card rounded-[18px] p-4 space-y-3">
        <SectionTitle>Цвет</SectionTitle>
        <AccentPicker botId={botId} />
      </section>

      <section className="surface-card rounded-[18px] p-4 space-y-3">
        <SectionTitle>Значок бренда</SectionTitle>
        <div className="flex gap-2">
          {GLYPHS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => save.mutate({ glyph: key })}
              aria-label={label}
              aria-pressed={data.glyph === key}
              className={`w-11 h-11 rounded-[13px] grid place-items-center transition-transform duration-100 active:scale-95 ${
                data.glyph === key
                  ? 'bg-white/[0.12] text-foreground'
                  : 'surface-inset text-muted-foreground'
              }`}
            >
              <Icon className="w-[19px] h-[19px]" />
            </button>
          ))}
        </div>
      </section>

      <section className="surface-card rounded-[18px] p-4 space-y-3">
        <SectionTitle>Когда просить отзыв</SectionTitle>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={60}
            defaultValue={data.ask_after_days}
            onBlur={(e) => {
              const v = Math.max(0, Math.min(60, Number(e.target.value) || 0));
              if (v !== data.ask_after_days) save.mutate({ ask_after_days: v });
            }}
            className="w-24 surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
          <span className="text-sm text-muted-foreground">дней после оплаты</span>
        </div>
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Бот один раз попросит оценить сервис. Ноль отключает просьбу. Сразу после оплаты
          спрашивать смысла нет: человек оценит скорость выдачи ключа, а не сам VPN.
        </p>
      </section>

      <section className="surface-card rounded-[18px] p-4 space-y-3">
        <SectionTitle>Скидка за отзыв</SectionTitle>
        <Row
          title="Благодарить скидкой"
          hint="Отзыв на 4-5 звёзд — клиент сразу получает скидку на следующую покупку. Код применяется сам, срок 14 дней."
          checked={data.reward?.enabled ?? false}
          onChange={(v) =>
            save.mutate({ reward: { enabled: v, percent: Number(rewardPercent) || 10 } })
          }
        />
        {(data.reward?.enabled ?? false) && (
          <>
            <Field label="Размер скидки, %">
              <input
                type="number"
                min={1}
                max={30}
                value={rewardPercent}
                onChange={(e) => setRewardPercent(e.target.value)}
                className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
              />
            </Field>
            <button
              type="button"
              onClick={() =>
                save.mutate({
                  reward: {
                    enabled: true,
                    percent: Math.max(1, Math.min(Number(rewardPercent) || 0, 30)),
                  },
                })
              }
              disabled={save.isPending || Number(rewardPercent) === data.reward?.percent}
              className="w-full bg-primary text-primary-foreground py-3 rounded-[13px] text-sm font-bold disabled:opacity-40 active:scale-[0.99] transition-transform"
            >
              Сохранить скидку
            </button>
            <p className="text-[11.5px] text-dim leading-relaxed">
              Скидку оплачиваете вы: она вычитается из вашей наценки по той покупке.
              Не хватит наценки, и разницу спишем с баланса, а без денег на нём скидка не
              применится. Больше 30% поставить нельзя.
            </p>
          </>
        )}
      </section>

      <section className="surface-card rounded-[18px] p-4 space-y-3">
        <SectionTitle>Промо-баннер</SectionTitle>
        <Row
          title="Показывать баннер"
          hint="Акция в шапке раздела отзывов."
          checked={data.promo.enabled}
          onChange={(v) =>
            save.mutate({ promo: { enabled: v, title: promoTitle, subtitle: promoSubtitle } })
          }
        />
        <Field label="Заголовок">
          <input
            value={promoTitle}
            onChange={(e) => setPromoTitle(e.target.value)}
            maxLength={60}
            placeholder="Минус 15% на первое продление"
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>
        <Field label="Подпись">
          <input
            value={promoSubtitle}
            onChange={(e) => setPromoSubtitle(e.target.value)}
            maxLength={80}
            placeholder="Промокод HEXVPN, до 31 августа"
            className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none"
          />
        </Field>
        <button
          type="button"
          onClick={() =>
            save.mutate({
              promo: { enabled: data.promo.enabled, title: promoTitle, subtitle: promoSubtitle },
            })
          }
          disabled={save.isPending || (promoTitle === data.promo.title && promoSubtitle === data.promo.subtitle)}
          className="w-full bg-primary text-primary-foreground py-3 rounded-[13px] text-sm font-bold disabled:opacity-40 active:scale-[0.99] transition-transform"
        >
          Сохранить баннер
        </button>
        {data.promo.enabled && !promoTitle.trim() && (
          <p className="text-[12px] text-danger">
            Без заголовка баннер не покажется: пустая рамка на витрине хуже, чем её отсутствие.
          </p>
        )}
      </section>

      <Link
        to="/bot-texts/$botId"
        params={{ botId }}
        className="flex items-center gap-3 p-3.5 rounded-[14px] surface-inset active:scale-[0.99] transition-transform"
      >
        <FileText className="w-4 h-4 text-primary-light shrink-0" />
        <span className="text-sm">
          Заголовок раздела и подпись «Рекомендует» правятся в текстах мини-аппа
        </span>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------- мелочи

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-sm font-semibold">{children}</div>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground mt-1.5">{hint}</p>}
    </div>
  );
}

function Row({
  title,
  hint,
  checked,
  onChange,
}: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{hint}</div>
      </div>
      <Switch checked={checked} aria-label={title} onCheckedChange={onChange} />
    </div>
  );
}

/** Карточка отзыва: автор с датой, пять звёзд справа, текст, кнопки модерации. */
function ReviewsSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-[18px] min-h-0 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0 space-y-2">
              <SkeletonLine className="w-1/3" />
              <SkeletonLine size="xs" className="w-1/2" />
            </div>
            <div className="flex gap-0.5 shrink-0">
              {[0, 1, 2, 3, 4].map((star) => (
                <Skeleton key={star} className="w-3.5 h-3.5 rounded-sm" />
              ))}
            </div>
          </div>
          <SkeletonParagraph lines={2} size="sm" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-24 rounded-lg" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
        </SkeletonCard>
      ))}
    </div>
  );
}
